import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack,
  Typography, Box, IconButton, alpha, CircularProgress, Chip, MenuItem, Select,
  FormControl, InputLabel, Alert
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  onClose: () => void;
  initialMedia?: { type: 'video' | 'screenshot' | 'none'; blob?: Blob; url?: string };
}

const BugReportDialog: React.FC<Props> = ({ open, onClose, initialMedia }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const pageUrl = typeof window !== 'undefined' ? window.location.pathname : '';
  const browserInfo = typeof navigator !== 'undefined' ? navigator.userAgent : '';

  useEffect(() => {
    if (!open) {
      setTitle('');
      setDescription('');
      setPriority('medium');
      setError('');
      setSuccess(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError(t('Başlık zorunludur'));
      return;
    }
    if (!initialMedia?.blob && !description.trim()) {
      setError(t('Açıklama veya medya ekleyin'));
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('description', description.trim());
      formData.append('priority', priority);
      formData.append('page_url', pageUrl);
      formData.append('browser_info', browserInfo);

      if (initialMedia?.blob) {
        const filename = initialMedia.type === 'video' ? 'screen-record.webm' : 'screenshot.png';
        formData.append('file', initialMedia.blob, filename);
      }

      await api.post('/bug-reports', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess(true);
      setTimeout(() => onClose(), 1500);
    } catch (err: any) {
      if (err.response?.status === 413) {
        setError(t('Dosya boyutu çok büyük. Lütfen daha kısa bir kayıt yapın.'));
      } else {
        setError(err.response?.data?.error || t('Gönderim başarısız'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography fontWeight={700}>{t('Hata Bildir')}</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ borderRadius: 2 }}>
            {t('Hata bildirimi gönderildi. Teşekkürler!')}
          </Alert>
        ) : (
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

            {/* Video preview */}
            {initialMedia?.type === 'video' && initialMedia.url && (
              <Box>
                <video src={initialMedia.url} controls style={{ width: '100%', borderRadius: 8, maxHeight: 240 }} />
                <Chip label={t('Ekran kaydı hazır')} size="small" color="success" sx={{ mt: 1 }} />
              </Box>
            )}

            {/* Screenshot preview */}
            {initialMedia?.type === 'screenshot' && initialMedia.url && (
              <Box>
                <Box component="img" src={initialMedia.url} alt={t('Screenshot')} sx={{ width: '100%', borderRadius: 2, border: `1px solid ${alpha('#000', 0.1)}` }} />
                <Chip label={t('Ekran görüntüsü hazır')} size="small" color="success" sx={{ mt: 1 }} />
              </Box>
            )}

            <TextField label={t('Başlık')} value={title} onChange={(e) => setTitle(e.target.value)} fullWidth
              placeholder={t('Kısa bir başlık yazın')}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />

            <TextField label={t('Açıklama')} value={description} onChange={(e) => setDescription(e.target.value)}
              fullWidth multiline rows={3} placeholder={t('Hatayı detaylı anlatın...')}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />

            <FormControl fullWidth size="small">
              <InputLabel>{t('Öncelik')}</InputLabel>
              <Select value={priority} label={t('Öncelik')} onChange={(e) => setPriority(e.target.value)} sx={{ borderRadius: 2 }}>
                <MenuItem value="low">{t('Düşük')}</MenuItem>
                <MenuItem value="medium">{t('Orta')}</MenuItem>
                <MenuItem value="high">{t('Yüksek')}</MenuItem>
                <MenuItem value="critical">{t('Kritik')}</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="caption" color="text.secondary">
              {t('Sayfa')}: {pageUrl}
            </Typography>
          </Stack>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} color="inherit" sx={{ borderRadius: 2 }}>{t('İptal')}</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)' }}>
            {submitting ? t('Gönderiliyor...') : t('Gönder')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default BugReportDialog;
